import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getLessons, updateLesson, getCaptures,
  getRecurring, materializeRecurring, dismissRecurring,
} from '../../lib/api'
import { keys } from '../../lib/queryKeys'
import { PageHeader } from '../../components/PageHeader'
import { Entry, EntryList, type EntryAccent } from '../../components/Entry'
import { m } from '../../paraglide/messages'
import type { Lesson, PendingCapture, RecurringPending } from '../../types'

export const Route = createFileRoute('/lessons/')({
  component: LessonsPage,
})

// Drop the year — day/month is enough for a working list.
const fmt = new Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', timeZone: 'Asia/Jerusalem' })
const fmtTime = new Intl.DateTimeFormat('he-IL', { timeStyle: 'short', timeZone: 'Asia/Jerusalem' })

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return fmt.format(new Date(iso))
}
function formatTime(iso: string | null) {
  if (!iso) return '—'
  return fmtTime.format(new Date(iso))
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
function slotLabel(r: RecurringPending): string {
  const h = Math.floor(r.timeMinutes / 60)
  const m = r.timeMinutes % 60
  const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  return `${WEEKDAYS[r.weekday] ?? '?'} · ${time} · ${r.durationMin} min`
}

// An "open" lesson is one that has already happened but is still unconfirmed
// (status 'scheduled'). Confirming it closes it.
function needsConfirmation(l: Lesson): boolean {
  return l.status === 'scheduled' && !!l.scheduledAt && new Date(l.scheduledAt) < new Date()
}

// Map a lesson to one of the three locked accents + a label.
function lessonStatus(l: Lesson): { accent: EntryAccent; label: string } {
  if (needsConfirmation(l)) return { accent: 'orange', label: m.status_waiting() }
  switch (l.status) {
    case 'completed': return { accent: 'green', label: m.status_completed() }
    case 'cancelled': return { accent: 'red', label: m.status_cancelled() }
    case 'no_show':   return { accent: 'red', label: m.status_no_show() }
    default:          return { accent: 'neutral', label: m.status_scheduled() }
  }
}

function sortLessons(lessons: Lesson[]): Lesson[] {
  const now = new Date()
  const future = lessons
    .filter((l) => l.scheduledAt && new Date(l.scheduledAt) >= now)
    .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime())
  const past = lessons
    .filter((l) => !l.scheduledAt || new Date(l.scheduledAt) < now)
    .sort((a, b) => {
      const ta = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0
      const tb = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0
      return tb - ta
    })
  return [...future, ...past]
}

function LessonsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<Lesson[]>({
    queryKey: keys.lessons,
    queryFn: getLessons,
  })

  const { data: captures } = useQuery<PendingCapture[]>({
    queryKey: keys.captures,
    queryFn:  getCaptures,
  })

  const cancel = useMutation({
    mutationFn: (id: number) => updateLesson(id, { scheduledAt: null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.lessons }),
  })

  if (isLoading) return <p className="text-zinc-400">{m.loading()}</p>
  if (error) return <p className="text-red-600 text-sm">{(error as Error).message}</p>

  const lessons = sortLessons(data ?? [])
  const openLessons = lessons.filter(needsConfirmation)
  const firstOpen   = openLessons.find(l => l.confirmToken)

  return (
    <div>
      <PageHeader title={m.lessons_title()}>
        <Link
          to="/lessons/new"
          className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
        >
          {m.lessons_new()}
        </Link>
      </PageHeader>

      {(captures ?? []).length > 0 && (
        <div className="mb-4 flex flex-col gap-2 border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm">
          <span className="text-zinc-600">
            {m.lessons_unmatched({ count: captures!.length })}
          </span>
          <div className="flex flex-col gap-1">
            {captures!.map((cap) => (
              <Link
                key={cap.id}
                to="/capture/$id"
                params={{ id: String(cap.id) }}
                className="flex items-center justify-between gap-3 text-zinc-700 hover:text-zinc-900 underline"
              >
                <span className="truncate">{cap.studentName}</span>
                <span className="shrink-0 text-xs">{m.lessons_create_student()}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <RecurringSection />

      {openLessons.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm">
          <span className="flex items-center gap-2 text-zinc-600">
            <span className="h-2 w-2 rounded-full bg-orange-500" />
            {m.lessons_waiting_count({ count: openLessons.length })}
          </span>
          {firstOpen?.confirmToken && (
            <Link
              to="/confirm/$token"
              params={{ token: firstOpen.confirmToken }}
              className="ms-auto border border-zinc-900 px-3 py-1 text-sm hover:bg-zinc-100"
            >
              {m.lessons_confirm_now()}
            </Link>
          )}
        </div>
      )}

      <EntryList isEmpty={lessons.length === 0} emptyMessage={m.lessons_empty()}>
        {lessons.map((lesson) => {
          const { accent, label } = lessonStatus(lesson)
          return (
            <Entry
              key={lesson.id}
              onClick={() => navigate({ to: '/lessons/$id', params: { id: String(lesson.id) } })}
              title={lesson.student?.name ?? '—'}
              meta={`${formatDate(lesson.scheduledAt)} · ${formatTime(lesson.scheduledAt)} · ${m.duration_min({ count: lesson.durationMin })}`}
              accent={accent}
              status={label}
              actions={
                needsConfirmation(lesson) && lesson.confirmToken ? (
                  <Link
                    to="/confirm/$token"
                    params={{ token: lesson.confirmToken }}
                    className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
                  >
                    {m.lessons_confirm()}
                  </Link>
                ) : lesson.status === 'scheduled' ? (
                  <button
                    onClick={() => cancel.mutate(lesson.id)}
                    disabled={cancel.isPending}
                    className="border border-red-700 text-red-700 px-3 py-1.5 text-sm hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {m.action_cancel()}
                  </button>
                ) : undefined
              }
            />
          )
        })}
      </EntryList>
    </div>
  )
}

// ─── Recurring — pending (collapsed series) ───────────────────────────────────

function RecurringSection() {
  const queryClient = useQueryClient()

  const { data } = useQuery<RecurringPending[]>({
    queryKey: keys.recurring,
    queryFn:  getRecurring,
  })

  const materialize = useMutation({
    mutationFn: (id: number) => materializeRecurring(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.recurring })
      queryClient.invalidateQueries({ queryKey: keys.lessons })
    },
  })

  const dismiss = useMutation({
    mutationFn: (id: number) => dismissRecurring(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.recurring }),
  })

  const rows = data ?? []
  if (rows.length === 0) return null

  return (
    <section className="mb-6">
      <h2 className="text-xs text-zinc-400 uppercase tracking-wide mb-2">{m.recurring_title()}</h2>
      <EntryList>
        {rows.map((r) => (
          <Entry
            key={r.id}
            title={r.student?.name ?? r.studentName}
            meta={slotLabel(r)}
            accent="orange"
            status={m.recurring_label()}
            actions={
              <>
                <button
                  onClick={() => materialize.mutate(r.id)}
                  disabled={materialize.isPending || r.studentId == null}
                  className="border border-zinc-900 px-2 py-1 text-xs hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {m.recurring_materialize()}
                </button>
                <button
                  onClick={() => dismiss.mutate(r.id)}
                  disabled={dismiss.isPending}
                  className="border border-red-700 text-red-700 px-2 py-1 text-xs hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {m.recurring_dismiss()}
                </button>
              </>
            }
          />
        ))}
      </EntryList>
    </section>
  )
}
